// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { DEFAULT_VALUES } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { UNSAFE_media } from "@atlaskit/primitives/responsive";
import { B50, N900 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Icon } from "@/libraries/wpl/foundations/src";

const sectionStyles = css`
    background: linear-gradient(0deg, #ffffff 0%, #e6fcff 100%);
    padding: ${token("space.1000")} ${token("space.200")} ${token("space.800")};

    .text-section {
        h4 {
            margin: 0 0 ${token("space.100")};
            font-weight: 500;

            @media (max-width: 47.99rem) {
                font-size: 1.25rem;
                line-height: 1.625rem;
            }

            @media (min-width: 48rem) and (max-width: 63.99rem) {
                font-size: 1.375rem;
                line-height: 1.75rem;
            }

            @media (min-width: 64rem) {
                font-size: 1.5rem;
                line-height: 1.875rem;
            }
        }

        p {
            margin: 0;
            font-weight: 400;
            color: ${N900};
            font-size: 1rem;
            line-height: 1.5rem;
        }
    }

    .contentBlock-body {
        color: #253858;
    }
`;

const sectionH2 = css`
    font-weight: 500;
    text-align: center;
    color: ${N900};
    margin: 0;

    @media (max-width: 47.99rem) {
        font-size: 2rem;
        line-height: 2.375rem;
    }

    @media (min-width: 48rem) and (max-width: 63.99rem) {
        font-size: 2.25rem;
        line-height: 2.625rem;
    }

    @media (min-width: 64rem) {
        font-size: 2.5rem;
        line-height: 3rem;
    }
`;

const outerGridStyles = css`
    max-width: 1200px;
    width: 100%;
    margin: ${token("space.200")} auto 0;
    display: grid;
    grid-template-columns: 1fr 1fr;

    ${UNSAFE_media.below.md} {
        grid-template-columns: 1fr;
    }
`;

const innerGridStyles = css`
    display: grid;
    grid-template-columns: 1fr 1fr;

    ${UNSAFE_media.below.sm} {
        grid-template-columns: 1fr;
    }
`;

const columnStyles = css`
    display: block;
    padding: ${token("space.200")};
`;

const iconStyles = css`
    display: flex;
    border-radius: 50%;
    background-color: ${B50};
    width: 60px;
    height: 60px;
    margin-bottom: ${token("space.200")};
`;

const innerIconStyles = css`
    margin: auto;
    width: min-content;
    height: min-content;
`;

const WhyConfluenceSection = () => {
    const intl = useIntl();

    const sectionContent = {
        communicationTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.communicationTitle",
            defaultMessage: "Visual communication"
        }),
        communicationBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.communicationBody",
            defaultMessage:
                "Bring your ideas to life: from brainstorming in whiteboards to project plans in pages."
        }),
        organizationScalesTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.organizationScalesTitle",
            defaultMessage: "Organization that scales"
        }),
        organizationScalesBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.organizationScalesBody",
            defaultMessage:
                "No matter your team size, projects and company knowledge are always findable and up-to-date."
        }),
        infoProtectionTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.infoProtectionTitle",
            defaultMessage: "Information protection"
        }),
        infoProtectionBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.infoProtectionBody",
            defaultMessage:
                "Permission settings let you control who can view or edit pages, keeping information private."
        }),
        aiPoweredTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.aiPoweredTitle",
            defaultMessage: "AI-powered productivity"
        }),
        aiPoweredBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.aiPoweredBody",
            defaultMessage:
                "Turn messy notes into useful docs, summarize in seconds, and search for relevant answers."
        })
    };

    return (
        <section css={sectionStyles}>
            <h2 css={sectionH2}>
                <FormattedMessage
                    id="ConfluenceSoftwarePageTemplate.whyConfluenceSection.header"
                    defaultMessage="Why Confluence?"
                    values={DEFAULT_VALUES}
                />
            </h2>

            <div css={outerGridStyles}>
                <div css={innerGridStyles}>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="lightbulb"
                                    size="small"
                                    color="n800"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.communicationTitle}
                            bodyText={sectionContent.communicationBody}
                        />
                    </div>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="building"
                                    size="small"
                                    color="n800"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.organizationScalesTitle}
                            bodyText={sectionContent.organizationScalesBody}
                        />
                    </div>
                </div>
                <div css={innerGridStyles}>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon name="lock" size="small" color="n800" />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.infoProtectionTitle}
                            bodyText={sectionContent.infoProtectionBody}
                        />
                    </div>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="circleunions"
                                    size="small"
                                    color="n800"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.aiPoweredTitle}
                            bodyText={sectionContent.aiPoweredBody}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyConfluenceSection;
